/**
 * Helpers for downloading a package's files from the browser.
 *
 * Two strategies:
 *
 * 1. `saveFilesToDirectory` – the user picks a destination folder ONCE (File System
 *    Access API) and every file is streamed straight into it. No per-file "Save As"
 *    dialog, regardless of the browser's "ask where to save" setting, and nothing is
 *    buffered in memory. Chrome/Edge only; requires the storage bucket to allow CORS
 *    GETs from the dashboard origin.
 *
 * 2. `startBrowserDownloads` – fallback for browsers without the API: click a hidden
 *    anchor per file and let the browser's download manager take over. Chrome asks
 *    once to allow multiple downloads; a "Save As" per file appears only if the user
 *    has "ask where to save each file" enabled.
 */
const DEFAULT_DELAY_MS = 600;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const isDirectoryPickerSupported = () =>
  typeof window !== "undefined" &&
  typeof window.showDirectoryPicker === "function" &&
  (typeof window.isSecureContext === "undefined" || window.isSecureContext);

/**
 * Open the folder picker. Must be called synchronously from a user gesture (a click
 * handler, before any `await`) or the browser rejects it. Returns null if the user
 * cancels the picker.
 */
export const pickDownloadDirectory = async () => {
  try {
    return await window.showDirectoryPicker({ mode: "readwrite", startIn: "downloads" });
  } catch (error) {
    if (error?.name === "AbortError") {
      return null;
    }
    throw error;
  }
};

/** Make a name safe for the local file system (the API rejects path separators). */
export const toSafeFileName = (name, fallback = "download") => {
  const cleaned = String(name || "")
    // eslint-disable-next-line no-control-regex
    .replace(/[/\\:*?"<>|\u0000-\u001f]/g, "_")
    .trim();
  return cleaned && cleaned !== "." && cleaned !== ".." ? cleaned : fallback;
};

/** Pick a file name that does not already exist in the folder: clip.mov, clip (1).mov … */
const uniqueFileName = async (directory, name) => {
  const dot = name.lastIndexOf(".");
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  let candidate = name;
  for (let i = 1; i < 1000; i += 1) {
    try {
      await directory.getFileHandle(candidate); // exists -> try next
      candidate = `${base} (${i})${ext}`;
    } catch (error) {
      if (error?.name === "NotFoundError") {
        return candidate;
      }
      throw error;
    }
  }
  return candidate;
};

/**
 * Create (or reuse) a subfolder inside the picked directory so a package's files stay
 * together. Also the practical answer to Chrome refusing the Downloads folder itself:
 * the user picks any allowed folder and the package folder is created inside it.
 */
export const createSubdirectory = async (directory, name) =>
  directory.getDirectoryHandle(toSafeFileName(name, "package"), { create: true });

export class DownloadCorsError extends Error {
  constructor(message) {
    super(message);
    this.name = "DownloadCorsError";
  }
}

/**
 * Stream every item into `directory`. Sequential so a slow link is not saturated by
 * a dozen parallel multi-GB transfers. Reports progress per file and per byte.
 *
 * @returns {Promise<{saved: number, cancelled: boolean}>}
 */
export const saveFilesToDirectory = async (
  items,
  directory,
  { onProgress, signal } = {}
) => {
  const downloadable = (items || []).filter((item) => item?.downloadUrl);
  let saved = 0;

  for (const item of downloadable) {
    if (signal?.aborted) {
      return { saved, cancelled: true };
    }

    const fileName = await uniqueFileName(directory, toSafeFileName(item.fileName));
    onProgress?.({ index: saved + 1, total: downloadable.length, fileName, received: 0, size: item.sizeBytes ?? null });

    let response;
    try {
      response = await fetch(item.downloadUrl, { signal, mode: "cors", credentials: "omit" });
    } catch (error) {
      if (error?.name === "AbortError") {
        return { saved, cancelled: true };
      }
      // A TypeError from fetch on a cross-origin URL almost always means the bucket
      // has no CORS rule for this origin; surface that distinctly so the UI can fall back.
      throw new DownloadCorsError(`Could not fetch ${fileName}: ${error?.message || error}`);
    }
    if (!response.ok) {
      throw new Error(`Download of ${fileName} failed with HTTP ${response.status}`);
    }

    const size = Number(response.headers.get("content-length")) || item.sizeBytes || null;
    const handle = await directory.getFileHandle(fileName, { create: true });
    const writable = await handle.createWritable();

    try {
      const reader = response.body.getReader();
      let received = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        await writable.write(value);
        received += value.byteLength;
        onProgress?.({ index: saved + 1, total: downloadable.length, fileName, received, size });
      }
      await writable.close();
    } catch (error) {
      await writable.abort().catch(() => {});
      if (error?.name === "AbortError" || signal?.aborted) {
        await directory.removeEntry(fileName).catch(() => {});
        return { saved, cancelled: true };
      }
      throw error;
    }

    saved += 1;
  }

  return { saved, cancelled: false };
};

export const triggerBrowserDownload = (url, fileName) => {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.rel = "noopener";
  if (fileName) {
    anchor.download = fileName;
  }
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

export const startBrowserDownloads = async (
  items,
  { delayMs = DEFAULT_DELAY_MS, onProgress } = {}
) => {
  const downloadable = (items || []).filter((item) => item?.downloadUrl);
  let started = 0;

  for (const item of downloadable) {
    triggerBrowserDownload(item.downloadUrl, item.fileName);
    started += 1;
    onProgress?.(started, downloadable.length);
    if (started < downloadable.length) {
      await wait(delayMs);
    }
  }

  return started;
};

export default startBrowserDownloads;
